import React, { useState, useRef } from 'react';
import { Upload, Link as LinkIcon, X, Loader2, GripVertical, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { uploadApi } from '../services/api';

/**
 * ImageManager Component
 * Allows uploading multiple images and adding image URLs
 * 
 * Props:
 * - images: array of image URLs
 * - onChange: callback when images change
 * - token: auth token for uploads
 * - maxImages: maximum number of images allowed (default 10)
 */
const ImageManager = ({ images = [], onChange, token, maxImages = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check max images limit
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed. You can add ${maxImages - images.length} more.`);
      return;
    }

    // Client-side file size check (5MB = 5 * 1024 * 1024 bytes)
    const MAX_SIZE = 5 * 1024 * 1024;
    const oversizedFiles = files.filter(f => f.size > MAX_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`).join(', ');
      setError(`File(s) too large (max 5MB): ${fileNames}`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      if (files.length === 1) {
        // Single file upload
        const result = await uploadApi.uploadImage(files[0], token);
        if (result.success && result.url) {
          onChange([...images, result.url]);
        } else {
          setError(result.detail || 'Upload failed');
        }
      } else {
        // Multiple files upload
        const result = await uploadApi.uploadImages(files, token);
        if (result.uploaded && result.uploaded.length > 0) {
          const newUrls = result.uploaded.map(img => img.url);
          onChange([...images, ...newUrls]);
        }
        if (result.errors && result.errors.length > 0) {
          const errorDetails = result.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
          setError(`${result.errors.length} file(s) failed:\n${errorDetails}`);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      // Display the detailed error message from backend
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAddUrl = () => {
    if (!urlInput.trim()) return;
    
    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    if (images.length >= maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    onChange([...images, urlInput.trim()]);
    setUrlInput('');
    setShowUrlInput(false);
    setError(null);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    // Reorder images
    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);
    
    setDraggedIndex(index);
    onChange(newImages);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <div 
          className="p-3 text-sm flex items-start justify-between gap-2"
          style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}
        >
          <span style={{ whiteSpace: 'pre-wrap' }}>{error}</span>
          <button onClick={() => setError(null)} className="flex-shrink-0 mt-0.5">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((url, index) => (
            <div
              key={index}
              className="relative group"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                opacity: draggedIndex === index ? 0.5 : 1,
                cursor: 'grab'
              }}
            >
              <div 
                className="aspect-square overflow-hidden"
                style={{ border: '1px solid var(--border-light)' }}
              >
                <img
                  src={url}
                  alt={`Room image ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200?text=Image+Error';
                  }}
                />
              </div>
              
              {/* Overlay with actions */}
              <div 
                className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
              >
                <div 
                  className="absolute top-2 left-2 p-1 bg-white rounded cursor-grab"
                  title="Drag to reorder"
                >
                  <GripVertical size={16} style={{ color: 'var(--text-secondary)' }} />
                </div>
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="p-2 bg-red-500 text-white rounded hover:bg-red-600"
                  title="Remove image"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Index badge */}
              <div 
                className="absolute bottom-2 right-2 px-2 py-1 text-xs font-medium"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)' }}
              >
                {index === 0 ? 'Main' : index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Controls */}
      <div className="flex flex-wrap gap-3">
        {/* File Upload Button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="image-upload"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || images.length >= maxImages}
          className="flex items-center gap-2"
          style={{ borderRadius: 0 }}
        >
          {uploading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
          {uploading ? 'Uploading...' : 'Upload Images'}
        </Button>

        {/* URL Input Toggle */}
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowUrlInput(!showUrlInput)}
          disabled={images.length >= maxImages}
          className="flex items-center gap-2"
          style={{ borderRadius: 0 }}
        >
          <LinkIcon size={16} />
          Add URL
        </Button>
      </div>

      {/* URL Input Field */}
      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{ borderRadius: 0, flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddUrl();
              }
            }}
          />
          <Button
            type="button"
            onClick={handleAddUrl}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Add
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setShowUrlInput(false);
              setUrlInput('');
            }}
            style={{ borderRadius: 0 }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Help Text */}
      <p className="text-xs" style={{ color: 'var(--text-light)' }}>
        {images.length}/{maxImages} images • Drag to reorder • First image is the main image
      </p>
    </div>
  );
};

export default ImageManager;
